import { isProduction } from '../utils/env';
import { getSiteAuthRuntime } from './bliss-app-auth';

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback;

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) return source.error;
  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }
  if (typeof source.message === 'string' && source.message) return source.message;
  if (source.data && typeof source.data.message === 'string' && source.data.message) return source.data.message;
  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function resolvePublicApiUrl(path: string): Promise<string> {
  // In production, prefer same-origin proxy routes to avoid browser CORS preflights
  // against the remote Bliss origin.
  if (isProduction()) {
    return `/api${path}`;
  }

  const runtime = await getSiteAuthRuntime();
  return `${runtime.apiBaseUrl}${path}`;
}

export async function requestPublicModelApplicationsApi<T>(
  path: string,
  options: RequestInit = {},
  fallbackError: string,
): Promise<T> {
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();

  if (options.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(await resolvePublicApiUrl(path), {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, fallbackError));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}
